import React, { useState, useCallback, useEffect, useRef } from 'react';
import './App.css';
import {
  BASE_ELEMENTS,
  getCombination,
  getElementColor,
  getElementInfo,
  getRandomBaseElement,
  getElementImageIndex,
  getRecipes,
  getAllElements,
  ELEMENTS_IMAGE_BASE,
} from './evolutionData';

const ROWS = 8;
const COLS = 8;
const EMPTY = '';
const STORAGE_KEY = 'evolutrix-save';
const MERGE_DURATION_MS = 320;
const SWAP_DURATION_MS = 280;
const DISAPPEAR_DURATION_MS = 220;
const FALL_DURATION_MS = 300;

type Grid = string[][];
type Cell = [number, number];

const NEIGHBOR_OFFSETS: Cell[] = [
  [-1, -1], [-1, 0], [-1, 1],
  [0, -1],           [0, 1],
  [1, -1],  [1, 0],  [1, 1],
];

function cellKey(c: Cell): string {
  return `${c[0]},${c[1]}`;
}

function createEmptyGrid(): Grid {
  return Array(ROWS).fill(null).map(() => Array(COLS).fill(EMPTY));
}

function fillGrid(grid: Grid): { grid: Grid; filled: Cell[] } {
  const newGrid = grid.map((row) => [...row]);
  const filled: Cell[] = [];
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      if (newGrid[r][c] === EMPTY) {
        newGrid[r][c] = getRandomBaseElement();
        filled.push([r, c]);
      }
    }
  }
  return { grid: newGrid, filled };
}

function getNeighbors(r: number, c: number): Cell[] {
  const neighbors: Cell[] = [];
  for (const [dr, dc] of NEIGHBOR_OFFSETS) {
    const nr = r + dr;
    const nc = c + dc;
    if (nr >= 0 && nr < ROWS && nc >= 0 && nc < COLS) {
      neighbors.push([nr, nc]);
    }
  }
  return neighbors;
}

function applyGravity(grid: Grid): { grid: Grid; fallDistances: number[][] } {
  const newGrid = grid.map((row) => [...row]);
  const fallDistances = Array(ROWS)
    .fill(null)
    .map(() => Array(COLS).fill(0));
  for (let c = 0; c < COLS; c++) {
    let write = ROWS - 1;
    for (let r = ROWS - 1; r >= 0; r--) {
      if (newGrid[r][c] !== EMPTY) {
        if (r !== write) {
          newGrid[write][c] = newGrid[r][c];
          fallDistances[write][c] = write - r;
          newGrid[r][c] = EMPTY;
        }
        write--;
      }
    }
  }
  return { grid: newGrid, fallDistances };
}

interface SavedGame {
  grid: Grid;
  score: number;
  evolutionLevel: number;
  evolutionLevelElement: string | null;
  unlockedCombinations: string[];
  allowAnyPair: boolean;
}

function loadSavedGame(): SavedGame | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as unknown;
    if (!data || typeof data !== 'object') return null;
    const o = data as Record<string, unknown>;
    const grid = o.grid;
    if (!Array.isArray(grid) || grid.length !== ROWS) return null;
    const validGrid: Grid = [];
    for (const row of grid) {
      if (!Array.isArray(row) || row.length !== COLS) return null;
      validGrid.push(row.map((c) => (typeof c === 'string' ? c : EMPTY)));
    }
    const score = typeof o.score === 'number' && o.score >= 0 ? o.score : 0;
    const evolutionLevel = typeof o.evolutionLevel === 'number' && o.evolutionLevel >= 1 ? o.evolutionLevel : 1;
    const evolutionLevelElement = o.evolutionLevelElement === null || typeof o.evolutionLevelElement === 'string' ? o.evolutionLevelElement : null;
    const rawUnlocked = o.unlockedCombinations;
    const unlockedCombinations = Array.isArray(rawUnlocked)
      ? rawUnlocked.filter((x: unknown) => typeof x === 'string') as string[]
      : [];
    const allowAnyPair = typeof o.allowAnyPair === 'boolean' ? o.allowAnyPair : false;
    return { grid: validGrid, score, evolutionLevel, evolutionLevelElement, unlockedCombinations, allowAnyPair };
  } catch {
    return null;
  }
}

function saveGameToStorage(state: SavedGame): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // quota or disabled
  }
}

function App(): JSX.Element {
  const [grid, setGrid] = useState<Grid>(() => fillGrid(createEmptyGrid()).grid);
  const [selected, setSelected] = useState<Cell | null>(null);
  const [score, setScore] = useState(0);
  const [evolutionLevel, setEvolutionLevel] = useState(1);
  const [evolutionLevelElement, setEvolutionLevelElement] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const [mergeAnimation, setMergeAnimation] = useState<{
    from: Cell;
    to: Cell;
    result: string;
    active: boolean;
  } | null>(null);
  const [invalidSwapAnimation, setInvalidSwapAnimation] = useState<{
    from: Cell;
    to: Cell;
    phase: 'forward' | 'back';
    active: boolean;
  } | null>(null);
  const [disappearingCells, setDisappearingCells] = useState<Set<string>>(new Set());
  const [newCells, setNewCells] = useState<Set<string>>(new Set());
  const [fallingCells, setFallingCells] = useState<Record<string, number>>({});
  const [showRestartModal, setShowRestartModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showTreeModal, setShowTreeModal] = useState(false);
  const [saveMessage, setSaveMessage] = useState<'success' | 'error' | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [failedImageIndices, setFailedImageIndices] = useState<Set<number>>(new Set());
  const [unlockedCombinations, setUnlockedCombinations] = useState<Set<string>>(new Set());
  const [allowAnyPair, setAllowAnyPair] = useState(false);
  const mergeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const invalidSwapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runGravityAndFill = useCallback(async (g: Grid) => {
    const { grid: gravityGrid, fallDistances } = applyGravity(g);
    const { grid: filledGrid, filled } = fillGrid(gravityGrid);

    const falling: Record<string, number> = {};
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        if (fallDistances[r][c] > 0) falling[cellKey([r, c])] = fallDistances[r][c];
      }
    }

    setGrid(filledGrid.map((row) => [...row]));
    setNewCells(new Set(filled.map(cellKey)));
    setFallingCells(falling);
    await new Promise((r) => setTimeout(r, FALL_DURATION_MS));
    setDisappearingCells(new Set());
    setNewCells(new Set());
    setFallingCells({});
    setResolving(false);
  }, []);

  useEffect(() => {
    if (!mergeAnimation) return;

    if (!mergeAnimation.active) {
      const t = requestAnimationFrame(() => {
        setMergeAnimation((prev) => (prev ? { ...prev, active: true } : null));
      });
      return () => cancelAnimationFrame(t);
    }

    mergeTimeoutRef.current = setTimeout(() => {
      const [r1, c1] = mergeAnimation.from;
      const [r2, c2] = mergeAnimation.to;
      const newGrid = grid.map((row) => [...row]);
      newGrid[r1][c1] = EMPTY;
      newGrid[r2][c2] = mergeAnimation.result;
      setGrid(newGrid);
      setMergeAnimation(null);
      setDisappearingCells(new Set());
      runGravityAndFill(newGrid);
    }, MERGE_DURATION_MS);

    return () => {
      if (mergeTimeoutRef.current) clearTimeout(mergeTimeoutRef.current);
    };
  }, [mergeAnimation, grid, runGravityAndFill]);

  useEffect(() => {
    if (!invalidSwapAnimation) return;

    if (!invalidSwapAnimation.active) {
      const t = requestAnimationFrame(() => {
        setInvalidSwapAnimation((prev) => (prev ? { ...prev, active: true } : null));
      });
      return () => cancelAnimationFrame(t);
    }

    invalidSwapTimeoutRef.current = setTimeout(() => {
      if (invalidSwapAnimation.phase === 'forward') {
        setInvalidSwapAnimation({
          ...invalidSwapAnimation,
          phase: 'back',
          active: false,
        });
      } else {
        setInvalidSwapAnimation(null);
        setResolving(false);
      }
    }, SWAP_DURATION_MS);

    return () => {
      if (invalidSwapTimeoutRef.current) clearTimeout(invalidSwapTimeoutRef.current);
    };
  }, [invalidSwapAnimation]);

  const handleCellClick = useCallback(
    (r: number, c: number) => {
      if (resolving || mergeAnimation || invalidSwapAnimation) return;
      const element = grid[r]?.[c];
      if (element === EMPTY) return;

      if (selected === null) {
        setSelected([r, c]);
        return;
      }

      const [sr, sc] = selected;
      if (sr === r && sc === c) {
        setSelected(null);
        return;
      }

      if (!allowAnyPair) {
        const neighbors = getNeighbors(sr, sc);
        const isNeighbor = neighbors.some(([nr, nc]) => nr === r && nc === c);
        if (!isNeighbor) {
          setSelected([r, c]);
          return;
        }
      }

      const a = grid[sr][sc];
      const b = grid[r][c];
      const combo = getCombination(a, b);
      if (!combo) {
        setSelected(null);
        setResolving(true);
        setInvalidSwapAnimation({
          from: [sr, sc],
          to: [r, c],
          phase: 'forward',
          active: false,
        });
        return;
      }

      setSelected(null);
      setResolving(true);
      setScore((s) => s + 1);
      setUnlockedCombinations((prev) => new Set(prev).add(combo.result));
      if (combo.complexity > evolutionLevel) {
        setEvolutionLevelElement(combo.result);
      }
      setEvolutionLevel((prev) => Math.max(prev, combo.complexity));
      setMergeAnimation({
        from: [sr, sc],
        to: [r, c],
        result: combo.result,
        active: false,
      });
    },
    [grid, selected, resolving, mergeAnimation, invalidSwapAnimation, evolutionLevel, allowAnyPair]
  );

  useEffect(() => {
    setGrid(fillGrid(createEmptyGrid()).grid);
  }, []);

  const handleRestart = useCallback(() => {
    setSelected(null);
    setScore(0);
    setEvolutionLevel(1);
    setEvolutionLevelElement(null);
    setUnlockedCombinations(new Set());
    setResolving(false);
    setFailedImageIndices(new Set());
    setMergeAnimation(null);
    setInvalidSwapAnimation(null);
    setDisappearingCells(new Set());
    setNewCells(new Set());
    setFallingCells({});
    setGrid(fillGrid(createEmptyGrid()).grid);
  }, []);

  const handleConfirmRestart = useCallback(() => {
    setShowRestartModal(false);
    handleRestart();
  }, [handleRestart]);

  const handleSave = useCallback(() => {
    try {
      saveGameToStorage({
        grid,
        score,
        evolutionLevel,
        evolutionLevelElement,
        unlockedCombinations: Array.from(unlockedCombinations),
        allowAnyPair,
      });
      setSaveMessage('success');
      setTimeout(() => setSaveMessage(null), 2000);
    } catch {
      setSaveMessage('error');
      setTimeout(() => setSaveMessage(null), 2000);
    }
  }, [grid, score, evolutionLevel, evolutionLevelElement, unlockedCombinations, allowAnyPair]);

  const applyLoadedGame = useCallback((saved: SavedGame) => {
    setGrid(saved.grid.map((row) => [...row]));
    setScore(saved.score);
    setEvolutionLevel(saved.evolutionLevel);
    setEvolutionLevelElement(saved.evolutionLevelElement);
    setUnlockedCombinations(new Set(saved.unlockedCombinations));
    setAllowAnyPair(saved.allowAnyPair);
    setSelected(null);
    setResolving(false);
    setMergeAnimation(null);
    setInvalidSwapAnimation(null);
    setDisappearingCells(new Set());
    setNewCells(new Set());
    setFallingCells({});
    setFailedImageIndices(new Set());
    setShowLoadModal(false);
  }, []);

  const hasCurrentProgress = score > 0 || evolutionLevel > 1;

  const handleLoadClick = useCallback(() => {
    const saved = loadSavedGame();
    if (!saved) {
      setSaveMessage('error');
      setTimeout(() => setSaveMessage(null), 2000);
      return;
    }
    if (hasCurrentProgress) {
      setShowLoadModal(true);
    } else {
      applyLoadedGame(saved);
    }
  }, [hasCurrentProgress, applyLoadedGame]);

  const handleConfirmLoad = useCallback(() => {
    const saved = loadSavedGame();
    if (saved) applyLoadedGame(saved);
    else setShowLoadModal(false);
  }, [applyLoadedGame]);

  const maxLevelForBackground = Math.min(evolutionLevel, 200);
  const backgroundStyle: React.CSSProperties = {
    ['--evolution-level' as string]: maxLevelForBackground,
  };

  const handleFullscreenToggle = useCallback(() => {
    try {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    } catch {
      // API non supportée ou refusée
    }
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  return (
    <div className="app evolutrix" style={backgroundStyle}>
      <header className="header">
        <button
          type="button"
          className="btn-fullscreen"
          onClick={handleFullscreenToggle}
          title={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
          aria-label={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
        >
          {isFullscreen ? '✕' : '⛶'}
        </button>
        <h1 className="header-title">Evolutrix</h1>
        <div className="header-stats">
          <span className="stat stat-evolution">
            Niveau d&apos;évolution : <strong>{evolutionLevel}</strong>
            {evolutionLevelElement != null && (
              <span className="stat-element" title={`Débloqué par : ${evolutionLevelElement}`}>
                <span className="stat-element-sep">·</span>
                <span className="stat-element-name">{evolutionLevelElement}</span>
              </span>
            )}
          </span>
          <span className="stat">
            Score : <strong>{score}</strong>
          </span>
        </div>
        <p className="hint">
          {allowAnyPair
            ? 'Sélectionnez deux éléments (n\'importe où sur la grille) pour les combiner. Le premier disparaît, le second devient le résultat.'
            : 'Sélectionnez un élément puis un voisin pour les combiner. Le premier disparaît, le second devient le résultat.'}
        </p>
        <div className="header-options">
          <label className="toggle-option">
            <input
              type="checkbox"
              checked={allowAnyPair}
              onChange={(e) => setAllowAnyPair(e.target.checked)}
              aria-describedby="toggle-any-pair-desc"
            />
            <span id="toggle-any-pair-desc">Combiner n&apos;importe où (toute la grille)</span>
          </label>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className={`btn-secondary btn-save ${saveMessage === 'success' ? 'btn-save-success' : ''} ${saveMessage === 'error' ? 'btn-save-error' : ''}`}
            onClick={handleSave}
            title="Sauvegarder la partie"
          >
            {saveMessage === 'success' ? 'Sauvegardé ✓' : 'Sauvegarder'}
          </button>
          <button type="button" className="btn-secondary" onClick={handleLoadClick} title="Charger une partie sauvegardée">
            Charger
          </button>
          <button type="button" className="btn-secondary" onClick={() => setShowTreeModal(true)} title="Voir l’arbre des évolutions découvertes">
            Arbre
          </button>
          <button type="button" className="btn-restart" onClick={() => setShowRestartModal(true)}>
            Recommencer
          </button>
        </div>
        {saveMessage === 'error' && <p className="save-feedback save-feedback-error">Aucune sauvegarde ou erreur</p>}
      </header>

      {showRestartModal && (
        <div className="modal-overlay" onClick={() => setShowRestartModal(false)} role="dialog" aria-modal="true" aria-labelledby="restart-modal-title">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <p id="restart-modal-title" className="modal-text">
              Êtes-vous sûr de vouloir recommencer ?
            </p>
            <div className="modal-actions">
              <button type="button" className="btn-modal btn-cancel" onClick={() => setShowRestartModal(false)}>
                Annuler
              </button>
              <button type="button" className="btn-modal btn-confirm" onClick={handleConfirmRestart}>
                Recommencer
              </button>
            </div>
          </div>
        </div>
      )}

      {showLoadModal && (
        <div className="modal-overlay" onClick={() => setShowLoadModal(false)} role="dialog" aria-modal="true" aria-labelledby="load-modal-title">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <p id="load-modal-title" className="modal-text">
              La partie en cours sera remplacée. Continuer ?
            </p>
            <div className="modal-actions">
              <button type="button" className="btn-modal btn-cancel" onClick={() => setShowLoadModal(false)}>
                Annuler
              </button>
              <button type="button" className="btn-modal btn-confirm" onClick={handleConfirmLoad}>
                Charger
              </button>
            </div>
          </div>
        </div>
      )}

      {showTreeModal && (() => {
        const renderTreeGem = (elementName: string, keySuffix: string) => {
          const color = getElementColor(elementName);
          const imageIndex = getElementImageIndex(elementName);
          const useImage = imageIndex > 0 && !failedImageIndices.has(imageIndex);
          const imageUrl = useImage ? `${ELEMENTS_IMAGE_BASE}${imageIndex}.png` : null;
          return (
            <div key={keySuffix} className="tree-gem" style={{ ['--element-color']: color } as React.CSSProperties}>
              <span className={`tree-gem-inner ${!useImage ? 'gem-fallback' : ''}`}>
                {useImage ? (
                  <>
                    <span className="tree-gem-bg" style={{ backgroundColor: color }} />
                    <img
                      src={imageUrl!}
                      alt=""
                      className="gem-bg-img"
                      onError={() => setFailedImageIndices((prev) => new Set(prev).add(imageIndex))}
                    />
                    <span className="gem-overlay" />
                  </>
                ) : null}
                <span className="tree-gem-name">{elementName}</span>
              </span>
            </div>
          );
        };

        const unlockedList = Array.from(unlockedCombinations)
          .map((name) => ({ name, info: getElementInfo(name) }))
          .filter((x): x is { name: string; info: NonNullable<typeof x.info> } => x.info != null)
          .sort((x, y) => x.info.complexity - y.info.complexity || x.name.localeCompare(y.name));

        const byLevel = new Map<number, { result: string; a: string; b: string }[]>();
        for (const { name: result } of unlockedList) {
          const recipes = getRecipes(result);
          if (recipes.length === 0) continue;
          const [a, b] = recipes[0];
          const info = getElementInfo(result);
          const complexity = info?.complexity ?? 0;
          const list = byLevel.get(complexity) ?? [];
          list.push({ result, a, b });
          byLevel.set(complexity, list);
        }
        const levels = Array.from(byLevel.entries()).sort(([c1], [c2]) => c1 - c2);

        return (
          <div className="modal-overlay" onClick={() => setShowTreeModal(false)} role="dialog" aria-modal="true" aria-labelledby="tree-modal-title">
            <div className="modal modal--tree" onClick={(e) => e.stopPropagation()}>
              <h2 id="tree-modal-title" className="modal-title">Arbre des évolutions</h2>
              <p className="modal-text modal-text--tree">Combinaisons que vous avez débloquées</p>
              <div className="tree-list">
                {levels.length === 0 ? (
                  <p className="tree-empty">Aucune combinaison débloquée encore.</p>
                ) : (
                  levels.map(([complexity, items]) => (
                    <div key={complexity} className="tree-level">
                      <div className="tree-level-title">Complexité {complexity}</div>
                      <div className="tree-level-items">
                        {items.map(({ result, a, b }) => (
                          <div key={`${a}-${b}-${result}`} className="tree-recipe">
                            {renderTreeGem(a, 'a')}
                            <span className="tree-plus" aria-hidden="true">+</span>
                            {renderTreeGem(b, 'b')}
                            <span className="tree-arrow" aria-hidden="true">→</span>
                            {renderTreeGem(result, 'r')}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-modal btn-cancel" onClick={() => setShowTreeModal(false)}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <main className="game-main">
      <div className="board-wrap">
        <div
          className="board"
          style={{ '--rows': ROWS, '--cols': COLS } as React.CSSProperties}
        >
          {grid.map((row, r) =>
            row.map((element, c) => {
              const key = cellKey([r, c]);
              const isEmpty = element === EMPTY;
              const isFirstSelected = selected?.[0] === r && selected?.[1] === c;
              const isMergeFrom = mergeAnimation && mergeAnimation.from[0] === r && mergeAnimation.from[1] === c;
              const isMergeTo = mergeAnimation && mergeAnimation.to[0] === r && mergeAnimation.to[1] === c;
              const displayElement = isMergeTo && mergeAnimation ? mergeAnimation.result : element;
              const fallRows = fallingCells[key];
              const fallStyle =
                fallRows != null && fallRows > 0
                  ? ({ '--fall-rows': fallRows } as React.CSSProperties)
                  : undefined;
              const color = displayElement ? getElementColor(displayElement) : 'transparent';
              const imageIndex = displayElement ? getElementImageIndex(displayElement) : 0;
              const useImage = imageIndex > 0 && !failedImageIndices.has(imageIndex);
              const imageUrl = useImage ? `${ELEMENTS_IMAGE_BASE}${imageIndex}.png` : null;

              const invFrom = invalidSwapAnimation?.from ?? [0, 0];
              const invTo = invalidSwapAnimation?.to ?? [0, 0];
              const isInvalidFrom = invalidSwapAnimation && invFrom[0] === r && invFrom[1] === c;
              const invalidSwapClass = isInvalidFrom ? 'fusion-back' : '';
              const invalidSwapActive = invalidSwapAnimation?.active ?? false;
              const invalidSwapBack = invalidSwapAnimation?.phase === 'back';
              const invalidSwapStyle =
                isInvalidFrom
                  ? ({ '--dr': invTo[0] - invFrom[0], '--dc': invTo[1] - invFrom[1] } as React.CSSProperties)
                  : undefined;

              const mergeFrom = mergeAnimation?.from ?? [0, 0];
              const mergeTo = mergeAnimation?.to ?? [0, 0];
              const mergeFlyStyle =
                isMergeFrom && mergeAnimation
                  ? ({ '--dr': mergeTo[0] - mergeFrom[0], '--dc': mergeTo[1] - mergeFrom[1] } as React.CSSProperties)
                  : undefined;

              return (
                <button
                  key={`${r}-${c}`}
                  type="button"
                  className={`cell ${isMergeFrom ? 'merge-from merge-fly' : ''} ${isMergeTo ? 'merge-to' : ''} ${mergeAnimation?.active ? 'merge-active' : ''} ${invalidSwapClass} ${invalidSwapActive ? 'fusion-active' : ''} ${invalidSwapBack ? 'fusion-back-phase' : ''} ${disappearingCells.has(key) ? 'disappearing' : ''} ${newCells.has(key) ? 'new-cell' : ''} ${fallRows != null && fallRows > 0 ? 'falling' : ''}`}
                  style={{
                    ...fallStyle,
                    ...invalidSwapStyle,
                    ...mergeFlyStyle,
                    ['--element-color' as string]: color,
                  }}
                  onClick={() => handleCellClick(r, c)}
                  data-selected={isFirstSelected}
                  data-empty={isEmpty}
                  disabled={resolving || isEmpty || !!invalidSwapAnimation}
                >
                  <span className={`gem-inner ${!useImage ? 'gem-fallback' : ''}`}>
                    {useImage ? (
                      <>
                        <span className="gem-bg" style={{ backgroundColor: color }} />
                        <img
                          src={imageUrl!}
                          alt=""
                          className="gem-bg-img"
                          onError={() => setFailedImageIndices((prev) => new Set(prev).add(imageIndex))}
                        />
                        <span className="gem-overlay" />
                      </>
                    ) : null}
                    {displayElement && (
                      <span className="element-name">{displayElement}</span>
                    )}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>
      </main>
    </div>
  );
}

export default App;
